<script setup lang="ts">
import BravoDialog from '@services/ui-component-library/components/BravoDialog.vue';
import BravoButton from '@services/ui-component-library/components/BravoButton.vue';
import ViewAsOrgUserModal from '@/components/ViewAsOrgUserModal.vue';
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { useUserStore } from '../stores/user';

const router = useRouter();
const authStore = useAuthStore();
const userStore = useUserStore();

const firstName = computed(() => userStore.userData?.first_name ?? '');

const ORG_USER_APP_URLS: Record<string, string> = {
    stage: 'https://appv5.stage-ovationcxm.app/',
    preprod: 'https://appv5.preprod-ovationcxm.app/',
    prod: 'https://app.ovationcxm.com/',
};

const orgUserAppUrl = computed<string | null>(() => {
    const explicit = import.meta.env.VITE_ORG_USER_APP_URL;
    if (explicit) return explicit;
    const env = import.meta.env.VITE_APP_ENV ?? 'local';
    return ORG_USER_APP_URLS[env] ?? null;
});

const showMissingUrlModal = ref(false);
const showOrgUserPicker = ref(false);

const goToSuperuserApp = async () => {
    if (authStore.redirectAfterLogin) {
        const redirect = authStore.redirectAfterLogin;

        if (redirect.startsWith('https')) {
            authStore.redirectAfterLogin = '';
            window.location.href = redirect;
            return;
        }

        const isValidRedirect =
            redirect !== '/login' &&
            !redirect.startsWith('/login?') &&
            redirect !== '/passwordreset' &&
            !redirect.startsWith('/passwordreset?') &&
            redirect !== '/login-admin-choice' &&
            !redirect.startsWith('/login-admin-choice?');

        if (isValidRedirect) {
            authStore.redirectAfterLogin = '';
            await router.push(redirect);
            return;
        }

        authStore.redirectAfterLogin = '';
    }

    await router.push('/organizations');
};

const viewAsOrgUser = () => {
    if (orgUserAppUrl.value) {
        showOrgUserPicker.value = true;
    } else {
        showMissingUrlModal.value = true;
    }
};

const handleOrgUserConfirm = ({ userId }: { userId: string; userName: string; orgId: string; orgName: string }) => {
    const base = orgUserAppUrl.value!.replace(/\/$/, '');
    window.location.href = `${base}/?actingAs=users:${userId}`;
};

const handleBack = async () => {
    await authStore.logout();
    router.push('/login');
};
</script>

<template>
    <div class="choice-page">
        <div class="logo-container">
            <img src="../assets/ovationcxm-login-logo.png" alt="OvationCXM Logo" />
        </div>

        <div class="choice-card">
            <h2 class="choice-title">Welcome back{{ firstName ? `, ${firstName}` : '' }}</h2>
            <p class="choice-subtitle">Where would you like to go?</p>

            <div class="choice-options">
                <button class="choice-option" @click="goToSuperuserApp">
                    <div class="option-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                            <path d="M2 17l10 5 10-5"/>
                            <path d="M2 12l10 5 10-5"/>
                        </svg>
                    </div>
                    <div class="option-content">
                        <span class="option-label">Superuser App</span>
                        <span class="option-description">Access the full admin experience</span>
                    </div>
                    <div class="option-arrow">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M9 18l6-6-6-6"/>
                        </svg>
                    </div>
                </button>

                <button class="choice-option" @click="viewAsOrgUser">
                    <div class="option-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                            <circle cx="9" cy="7" r="4"/>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                    </div>
                    <div class="option-content">
                        <span class="option-label">View CXME as an Organization User</span>
                        <span class="option-description">Experience CXME from an org perspective</span>
                    </div>
                    <div class="option-arrow">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M9 18l6-6-6-6"/>
                        </svg>
                    </div>
                </button>
            </div>
        </div>

        <button class="back-btn" @click="handleBack" aria-label="Back to login">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M15 18l-6-6 6-6"/>
            </svg>
            Go Back
        </button>

        <ViewAsOrgUserModal
            v-if="showOrgUserPicker"
            :visible="showOrgUserPicker"
            @update:visible="showOrgUserPicker = $event"
            @confirm="handleOrgUserConfirm"
        />

        <BravoDialog
            v-if="showMissingUrlModal"
            :visible="showMissingUrlModal"
            modal
            header="Configuration Error"
            :style="{ width: '400px' }"
            :closable="true"
            @update:visible="showMissingUrlModal = $event"
        >
            <p class="missing-url-message">
                No organization user app URL is configured for this environment.
                Set <code>VITE_ORG_USER_APP_URL</code> in your environment variables.
            </p>

            <template #footer>
                <div class="dialog-footer">
                    <BravoButton
                        label="OK"
                        @click="showMissingUrlModal = false"
                    />
                </div>
            </template>
        </BravoDialog>
    </div>
</template>

<style scoped>
.choice-page {
    background-image: url('../assets/login-ovationcxm-background.svg');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    min-height: 100vh;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2rem;
}

.logo-container {
    max-width: 300px;
}

.logo-container img {
    width: 100%;
    height: auto;
}

.choice-card {
    background: var(--color-surface, #ffffff);
    border-radius: 12px;
    padding: 2rem;
    width: 100%;
    max-width: 440px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.back-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    background: none;
    border: none;
    padding: 0.25rem 0.5rem;
    font-size: 0.9375rem;
    font-weight: 500;
    color: var(--primary-color, #2563eb);
    cursor: pointer;
    transition: opacity 0.15s ease;
}

.back-btn:hover {
    opacity: 0.75;
}

.back-btn svg {
    width: 15px;
    height: 15px;
}

.choice-title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--text-color, #1a1a1a);
    text-align: center;
}

.choice-subtitle {
    margin: -1rem 0 0;
    font-size: 0.9375rem;
    color: var(--text-color-secondary, #6b7280);
    text-align: center;
}

.choice-options {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.choice-option {
    display: flex;
    align-items: center;
    gap: 1rem;
    width: 100%;
    padding: 1rem 1.25rem;
    border: 1.5px solid var(--surface-border, #e5e7eb);
    border-radius: 10px;
    background: var(--surface-card, #ffffff);
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
    text-align: left;
}

.choice-option:hover {
    border-color: var(--primary-color, #4f46e5);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary-color, #4f46e5) 12%, transparent);
    background: var(--surface-hover, #f9fafb);
}

.choice-option:active {
    transform: scale(0.99);
}

.option-icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: color-mix(in srgb, var(--primary-color, #4f46e5) 10%, transparent);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-color, #4f46e5);
}

.option-icon svg {
    width: 20px;
    height: 20px;
}

.option-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
}

.option-label {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--text-color, #1a1a1a);
}

.option-description {
    font-size: 0.8125rem;
    color: var(--text-color-secondary, #6b7280);
}

.option-arrow {
    flex-shrink: 0;
    color: var(--text-color-secondary, #9ca3af);
}

.option-arrow svg {
    width: 18px;
    height: 18px;
}

.missing-url-message {
    margin: 0;
    font-size: 0.9375rem;
    color: var(--text-color, #1a1a1a);
    line-height: 1.5;
}

.missing-url-message code {
    font-family: monospace;
    font-size: 0.875rem;
    background: var(--surface-ground, #f4f4f5);
    padding: 0.1em 0.35em;
    border-radius: 4px;
}

.dialog-footer {
    display: flex;
    justify-content: flex-end;
}
</style>
